import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Input,
    Textarea,
    Select,
    Option,
    Button,
    Typography,
} from '@material-tailwind/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../AuthContext';

const CreateTaskModal = ({ open, onClose, onTaskCreated, projectId, defaultStatus }) => {
    const { rootUrl } = require('../../../config/config');
    const { user, token: authToken } = useAuth();
    const token = authToken || localStorage.getItem('authToken') || '';

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        priority: 'normal',
        status: defaultStatus || 'open',
        project_id: projectId || null,
        due_date: '',
        start_date: '',
        estimated_hours: '',
        assigned_to: [],
        tags: [],
        billable: false,
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersError, setUsersError] = useState('');

    useEffect(() => {
        if (!open) return;
        if (!projectId) fetchProjects();
        fetchUsers();
    }, [open, token]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchProjects = async () => {
        try {
            const response = await axios.get(`${rootUrl}/api/projects/user/current`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) setProjects(response.data.data);
        } catch (error) {
            console.error('Error fetching projects:', error);
        }
    };

    const fetchUsers = async () => {
        setUsersLoading(true);
        setUsersError('');
        try {
            const response = await axios.get(`${rootUrl}/api/projects/users`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) setUsers(response.data.data || []);
        } catch (error) {
            console.error('Error fetching users:', error);
            setUsersError('Could not load users.');
        } finally {
            setUsersLoading(false);
        }
    };

    const toggleAssignee = (userId) => {
        setFormData(prev => ({
            ...prev,
            assigned_to: prev.assigned_to.includes(userId)
                ? prev.assigned_to.filter(id => id !== userId)
                : [...prev.assigned_to, userId],
        }));
    };

    const handleChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => { const e = { ...prev }; delete e[name]; return e; });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.title) newErrors.title = 'Task title is required';
        if (formData.title && formData.title.length > 500)
            newErrors.title = 'Title must be 500 characters or less';
        if (formData.estimated_hours && (isNaN(formData.estimated_hours) || formData.estimated_hours < 0))
            newErrors.estimated_hours = 'Estimated hours must be a positive number';
        if (formData.due_date && formData.start_date && new Date(formData.due_date) < new Date(formData.start_date))
            newErrors.due_date = 'Due date cannot be before start date';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        try {
            const taskData = {
                ...formData,
                created_by: user?.id ?? null,
                estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
            };

            const response = await axios.post(`${rootUrl}/api/tasks`, taskData, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.data.success) {
                onTaskCreated(response.data.data);
                resetForm();
            }
        } catch (error) {
            console.error('Error creating task:', error);
            setErrors({ submit: error.response?.data?.message || 'Failed to create task' });
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            priority: 'normal',
            status: defaultStatus || 'open',
            project_id: projectId || null,
            due_date: '',
            start_date: '',
            estimated_hours: '',
            assigned_to: [],
            tags: [],
            billable: false,
        });
        setErrors({});
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const getInitials = (u) => {
        if (u.name) {
            return u.name.split(' ').map(p => p[0]).join('').substring(0, 2).toUpperCase();
        }
        return (u.username || '').substring(0, 2).toUpperCase();
    };

    return (
        <Dialog open={open} handler={handleClose} size="lg" className="max-h-[90vh] overflow-auto">
            <DialogHeader className="flex justify-between items-center">
                <Typography variant="h5">Create New Task</Typography>
                <button onClick={handleClose} className="text-gray-500 hover:text-gray-700">
                    <XMarkIcon className="h-6 w-6" />
                </button>
            </DialogHeader>

            <form onSubmit={handleSubmit}>
                <DialogBody className="space-y-4 overflow-y-auto max-h-[60vh]">
                    <div>
                        <Input
                            label="Task Title *"
                            value={formData.title}
                            onChange={e => handleChange('title', e.target.value)}
                            error={!!errors.title}
                            size="lg"
                        />
                        {errors.title && (
                            <Typography variant="small" color="red" className="mt-1">{errors.title}</Typography>
                        )}
                    </div>

                    <Textarea
                        label="Description"
                        value={formData.description}
                        onChange={e => handleChange('description', e.target.value)}
                        rows={4}
                    />

                    {!projectId && projects.length > 0 && (
                        <Select
                            label="Workflow"
                            value={formData.project_id}
                            onChange={val => handleChange('project_id', val)}
                        >
                            {projects.map(project => (
                                <Option key={project.id} value={project.id}>{project.name}</Option>
                            ))}
                        </Select>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <Select label="Priority" value={formData.priority} onChange={val => handleChange('priority', val)}>
                            <Option value="low">Low</Option>
                            <Option value="normal">Normal</Option>
                            <Option value="high">High</Option>
                            <Option value="urgent">Urgent</Option>
                        </Select>
                        <Select label="Status" value={formData.status} onChange={val => handleChange('status', val)}>
                            <Option value="open">Open</Option>
                            <Option value="To Do">To Do</Option>
                            <Option value="In Progress">In Progress</Option>
                            <Option value="Review">Review</Option>
                            <Option value="Done">Done</Option>
                        </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Input
                                type="date"
                                label="Start Date"
                                value={formData.start_date}
                                onChange={e => handleChange('start_date', e.target.value)}
                            />
                        </div>
                        <div>
                            <Input
                                type="date"
                                label="Due Date"
                                value={formData.due_date}
                                onChange={e => handleChange('due_date', e.target.value)}
                                error={!!errors.due_date}
                            />
                            {errors.due_date && (
                                <Typography variant="small" color="red" className="mt-1">{errors.due_date}</Typography>
                            )}
                        </div>
                    </div>

                    <div>
                        <Input
                            type="number"
                            label="Estimated Hours"
                            value={formData.estimated_hours}
                            onChange={e => handleChange('estimated_hours', e.target.value)}
                            error={!!errors.estimated_hours}
                            min="0"
                            step="0.5"
                        />
                        {errors.estimated_hours && (
                            <Typography variant="small" color="red" className="mt-1">{errors.estimated_hours}</Typography>
                        )}
                    </div>

                    {/* Assignee avatar grid */}
                    <div>
                        <Typography variant="small" className="mb-2 font-semibold text-gray-700">
                            Assign To
                        </Typography>
                        {usersLoading ? (
                            <div className="flex gap-3">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="flex flex-col items-center gap-1">
                                        <div className="w-9 h-9 rounded-full bg-gray-200 animate-pulse" />
                                        <div className="w-10 h-2 bg-gray-200 rounded animate-pulse" />
                                    </div>
                                ))}
                            </div>
                        ) : usersError ? (
                            <Typography variant="small" color="red">{usersError}</Typography>
                        ) : users.length === 0 ? (
                            <Typography variant="small" color="gray">No users available.</Typography>
                        ) : (
                            <div className="flex flex-wrap gap-3">
                                {users.map(u => {
                                    const selected = formData.assigned_to.includes(u.id);
                                    return (
                                        <button
                                            key={u.id}
                                            type="button"
                                            onClick={() => toggleAssignee(u.id)}
                                            className="flex flex-col items-center gap-1 focus:outline-none"
                                        >
                                            <div
                                                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                                                    selected
                                                        ? 'bg-[#FF5733] text-white ring-2 ring-[#FF5733] ring-offset-1'
                                                        : 'bg-gray-200 text-gray-600 border-2 border-dashed border-gray-300'
                                                }`}
                                            >
                                                {getInitials(u)}
                                            </div>
                                            <span className="text-xs text-gray-500 max-w-[48px] truncate">
                                                {u.name ? u.name.split(' ')[0] : u.username}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="billable"
                            checked={formData.billable}
                            onChange={e => handleChange('billable', e.target.checked)}
                            className="h-4 w-4 text-[#FF5733] rounded"
                        />
                        <label htmlFor="billable" className="text-sm text-gray-700">Billable</label>
                    </div>

                    {errors.submit && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <Typography variant="small" color="red">{errors.submit}</Typography>
                        </div>
                    )}
                </DialogBody>

                <DialogFooter className="space-x-2">
                    <Button variant="outlined" onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        className="bg-[#FF5733] hover:bg-[#E64A2E]"
                        disabled={loading}
                    >
                        {loading ? 'Creating...' : 'Create Task'}
                    </Button>
                </DialogFooter>
            </form>
        </Dialog>
    );
};

export default CreateTaskModal;
